import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useImprentaEvento,
  useImprentaEventos,
  useProbarTfhka,
  useTfhkaEstado,
} from '../api/queries-admin';
import { Modal } from '../components/Modal';
import { formatDate, formatTime } from '../lib/format';

const ESTADOS = ['', 'EMITIDO', 'ERROR', 'RETENCION', 'ANULADO', 'ENVIADO'];

export function ImprentaPage() {
  const { user } = useAuth();
  const [estado, setEstado] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const eventos = useImprentaEventos({
    estado: estado || undefined,
    search: search || undefined,
    page,
    limit: 25,
  });
  const detalle = useImprentaEvento(selectedId);
  const estadoTfhka = useTfhkaEstado();
  const probar = useProbarTfhka();
  const [conexion, setConexion] = useState<{
    ok: boolean;
    configurado: boolean;
    baseUrl: string;
    expiraEn?: string;
    mensaje: string;
  } | null>(null);
  const rows = eventos.data?.data ?? [];
  const tfhka = estadoTfhka.data;

  const probarConexion = async () => {
    try {
      const result = await probar.mutateAsync();
      setConexion(result.data);
    } catch (error) {
      setConexion({
        ok: false,
        configurado: true,
        baseUrl: '',
        mensaje: error instanceof Error ? error.message : 'No se pudo verificar TFHKA.',
      });
    }
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Administración fiscal</p>
          <h1 className="page-title">Panel TFHKA</h1>
          <p className="page-sub">
            Conexión con la imprenta digital, emisión y bitácora de documentos. Visible
            únicamente para administradores.
          </p>
        </div>
        <div className="page-head__actions">
          <button
            type="button"
            className="btn btn--outline"
            onClick={() => void probarConexion()}
            disabled={probar.isPending}
          >
            {probar.isPending ? 'Verificando…' : 'Probar conexión TFHKA'}
          </button>
        </div>
      </header>

      <div className="summary-strip summary-strip--4">
        <div className={`summary-tile${tfhka?.configurado ? ' summary-tile--accent' : ' summary-tile--warning'}`}>
          <span>Servicio TFHKA</span>
          <strong>
            {estadoTfhka.isLoading
              ? 'Consultando…'
              : tfhka?.configurado
                ? 'Configurado'
                : 'Sin configurar'}
          </strong>
          <small>
            {tfhka?.configurado
              ? 'Credenciales en el servidor'
              : 'Faltan TFHKA_USUARIO / TFHKA_CLAVE'}
          </small>
        </div>
        <div className="summary-tile">
          <span>Ambiente</span>
          <strong>
            {tfhka?.ambiente === 'DEMO'
              ? 'Demo'
              : tfhka?.ambiente === 'PRODUCCION'
                ? 'Producción'
                : '—'}
          </strong>
          <small className="mono" title={tfhka?.baseUrl}>
            {tfhka?.baseUrl ?? 'Sin URL configurada'}
          </small>
        </div>
        <div className={`summary-tile${tfhka?.errores24h ? ' summary-tile--warning' : ''}`}>
          <span>Eventos 24 h</span>
          <strong>{tfhka?.eventos24h ?? '—'}</strong>
          <small>{tfhka?.errores24h ?? 0} errores</small>
        </div>
        <div className="summary-tile">
          <span>Última actividad</span>
          <strong>
            {tfhka?.ultimoEventoAt ? formatDate(tfhka.ultimoEventoAt) : 'Sin eventos'}
          </strong>
          <small>
            {tfhka?.ultimoEventoAt
              ? formatTime(tfhka.ultimoEventoAt)
              : 'La bitácora está vacía'}
          </small>
        </div>
      </div>

      {conexion ? (
        <div className={`inline-note${conexion.ok ? '' : ' inline-note--warning'}`} role="status">
          <strong>{conexion.ok ? 'Conexión TFHKA verificada.' : 'TFHKA no respondió correctamente.'}</strong>{' '}
          {conexion.mensaje}
          {conexion.baseUrl ? ` · ${conexion.baseUrl}` : ''}
          {conexion.ok && conexion.expiraEn
            ? ` · Token válido hasta ${new Date(conexion.expiraEn).toLocaleString('es-VE')}`
            : ''}
        </div>
      ) : null}

      <section className="card clients-card">
        <div className="clients-toolbar">
          <div className="filter-controls">
            <select
              className="input"
              value={estado}
              onChange={(event) => {
                setEstado(event.target.value);
                setPage(1);
              }}
              aria-label="Filtrar por estado"
            >
              {ESTADOS.map((value) => (
                <option key={value || 'todos'} value={value}>
                  {value || 'Todos los estados'}
                </option>
              ))}
            </select>
            <div className="search-input">
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Documento, control o mensaje…"
                aria-label="Buscar eventos de imprenta"
              />
            </div>
          </div>
          <span className="pill pill--soft">{eventos.data?.meta.total ?? 0} eventos</span>
        </div>

        <div className="table-scroll">
          <table className="data-table data-table--compact">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Documento</th>
                <th>Endpoint</th>
                <th>Estado</th>
                <th>Control</th>
                <th>Mensaje</th>
                <th>Usuario</th>
                <th>HTTP</th>
              </tr>
            </thead>
            <tbody>
              {eventos.isLoading ? <tr><td colSpan={8}><div className="table-empty">Cargando bitácora…</div></td></tr> : null}
              {!eventos.isLoading && rows.length === 0 ? <tr><td colSpan={8}><div className="table-empty">Sin eventos de imprenta.</div></td></tr> : null}
              {rows.map((row) => (
                <tr key={row.id} className="row-clickable" onClick={() => setSelectedId(row.id)}>
                  <td>
                    <div className="doc-cell">
                      <strong>{formatDate(row.createdAt)}</strong>
                      <span>{formatTime(row.createdAt)}</span>
                    </div>
                  </td>
                  <td>{row.numeroDocumento ?? '—'}<br /><small>{row.documentoTipo ?? '—'}</small></td>
                  <td><span className="mono">{row.endpoint}</span></td>
                  <td>
                    <span className={`status-badge status-badge--${row.estado === 'ERROR' ? 'danger' : row.estado === 'EMITIDO' || row.estado === 'RETENCION' ? 'success' : 'warning'}`}>
                      {row.estado}
                    </span>
                  </td>
                  <td>{row.numeroControl ?? '—'}</td>
                  <td>{row.mensaje ?? '—'}</td>
                  <td>{row.usuarioNombre ?? 'Sistema'}</td>
                  <td>{row.httpStatus ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>Página {page} de {eventos.data?.meta.pages ?? 1}</span>
          <div className="row-actions">
            <button type="button" className="btn btn--outline btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <button type="button" className="btn btn--outline btn--sm" disabled={page >= (eventos.data?.meta.pages ?? 1)} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
          </div>
        </footer>
      </section>

      {selectedId ? (
        <Modal
          title={detalle.data ? `${detalle.data.numeroDocumento ?? 'Evento'} · ${detalle.data.estado}` : 'Evento de imprenta'}
          eyebrow={detalle.data ? `${detalle.data.endpoint} · ${detalle.data.usuarioNombre ?? 'Sistema'}` : 'TFHKA'}
          onClose={() => setSelectedId(null)}
          wide
          footer={<button type="button" className="btn btn--outline" onClick={() => setSelectedId(null)}>Cerrar</button>}
        >
          <div className="modal-card__body">
            {detalle.isLoading ? <p className="muted">Cargando respuesta…</p> : null}
            {detalle.data ? (
              <>
                <div className="detail-grid">
                  <div className="detail-block"><span>Fecha</span><strong>{formatDate(detalle.data.createdAt)}</strong><small>{formatTime(detalle.data.createdAt)}</small></div>
                  <div className="detail-block"><span>HTTP</span><strong>{detalle.data.httpStatus ?? '—'}</strong><small>{detalle.data.duracionMs ?? '—'} ms</small></div>
                  <div className="detail-block"><span>Control</span><strong>{detalle.data.numeroControl ?? '—'}</strong><small>{detalle.data.transaccionId ?? 'Sin transacción'}</small></div>
                  <div className="detail-block"><span>Mensaje</span><strong>{detalle.data.mensaje ?? 'Sin mensaje'}</strong><small>{detalle.data.proveedor}</small></div>
                </div>
                <h3 className="card-title">Request enviado</h3>
                <pre className="code-block">{JSON.stringify(detalle.data.request, null, 2)}</pre>
                <h3 className="card-title">Respuesta / error</h3>
                <pre className="code-block">{JSON.stringify(detalle.data.response, null, 2)}</pre>
              </>
            ) : null}
          </div>
        </Modal>
      ) : null}

      <p className="page-footnote">
        Solo el administrador puede ver esta bitácora. Los cuerpos se conservan para auditoría
        y diagnóstico de la imprenta digital.
      </p>
    </div>
  );
}
